// src/components/sensors/sensorDetailsInsights.js
// FlowSense AI — per-sensor insights from real data — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// Pure functions (no React) used by ViewSensorDetails:
//   mergeReadings()       recent readings from /context + the live feed, de-duplicated
//   readingStats()        count, min / max / avg, cadence, gaps, level transitions
//   buildSensorInsights() ranked findings the operator should act on
// Every finding is computed from stored readings, the live feed, the sensor
// record, alerts / work orders and the AI view-model (sensorAiInsights). Nothing
// is estimated; a check without enough data is skipped, not guessed.
// ═══════════════════════════════════════════════════════════════════════════
import { formatAgo, formatDurationShort, readingTimeMs, toMillis } from './sensorLiveStatus';

export const EXPECTED_CADENCE_SEC = 15;   // CaribTech nodes post every 15 s (firmware PRESSURE_POST_MS / WATER_HEARTBEAT_MS)
export const GAP_SEC = 60;                // ≥ 4 missed posts in a row
export const PSI_LOW = 20;
export const PSI_HIGH = 100;
export const LEVELS = ['EMPTY', 'LOW', 'MEDIUM', 'HIGH'];
const SEVERITY_RANK = { critical: 4, warning: 3, info: 2, ok: 1 };

const median = (arr) => {
  if (!arr.length) return null;
  const a = [...arr].sort((x, y) => x - y);
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

const valueOf = (kind, r) => (kind === 'water_level'
  ? r?.level ?? r?.water_level ?? r?.value
  : r?.psi ?? r?.pressure ?? r?.value);

/**
 * @param {object}   p
 * @param {'pressure'|'water_level'} p.kind
 * @param {object[]} [p.contextReadings] newest first: { value, timestamp }
 * @param {object[]} [p.liveRecent]      oldest → newest, from useSensorLiveFeed
 * @param {object[]} [p.extra]           other readings (e.g. the page's liveReading)
 * @returns {{t:number, value:number, level:string|null}[]} oldest → newest
 */
export function mergeReadings({ kind, contextReadings = [], liveRecent = [], extra = [], max = 240 }) {
  const out = new Map();
  const add = (t, raw) => {
    if (t === null || t === undefined || !Number.isFinite(t)) return;
    let value;
    let level = null;
    if (kind === 'water_level') {
      level = String(raw ?? '').toUpperCase();
      value = LEVELS.indexOf(level);
      if (value < 0) return;
    } else {
      value = Number(raw);
      if (raw === null || raw === undefined || raw === '' || !Number.isFinite(value)) return;
    }
    out.set(`${t}|${value}`, { t, value, level });
  };
  contextReadings.forEach((r) => add(toMillis(r?.timestamp), valueOf(kind, r)));
  [...liveRecent, ...extra].forEach((r) => r && add(toMillis(r.timestamp) ?? readingTimeMs(r), valueOf(kind, r)));
  return [...out.values()].sort((a, b) => a.t - b.t).slice(-max);
}

/** Stats over merged points (oldest → newest). */
export function readingStats(points, kind) {
  const n = points.length;
  const s = { n, firstMs: n ? points[0].t : null, lastMs: n ? points[n - 1].t : null };
  s.spanMs = n > 1 ? s.lastMs - s.firstMs : 0;
  const intervals = [];
  for (let i = 1; i < n; i += 1) {
    const d = (points[i].t - points[i - 1].t) / 1000;
    if (d > 0) intervals.push(d);
  }
  s.medianIntervalSec = median(intervals);
  s.maxGapSec = intervals.length ? Math.max(...intervals) : null;
  s.gaps = intervals.filter((d) => d >= GAP_SEC).length;
  if (kind === 'water_level') {
    const counts = { EMPTY: 0, LOW: 0, MEDIUM: 0, HIGH: 0 };
    let transitions = 0;
    points.forEach((p, i) => {
      counts[LEVELS[p.value]] += 1;
      if (i && p.value !== points[i - 1].value) transitions += 1;
    });
    s.counts = counts;
    s.transitions = transitions;
    s.last = n ? LEVELS[points[n - 1].value] : null;
  } else {
    const v = points.map((p) => p.value);
    s.min = n ? Math.min(...v) : null;
    s.max = n ? Math.max(...v) : null;
    s.avg = n ? v.reduce((a, b) => a + b, 0) / n : null;
    s.last = n ? v[n - 1] : null;
    s.prev = n > 1 ? v[n - 2] : null;
    s.above = v.filter((x) => x > PSI_HIGH).length;
    s.below = v.filter((x) => x < PSI_LOW).length;
    s.stdev = n > 1 ? Math.sqrt(v.reduce((a, x) => a + (x - s.avg) ** 2, 0) / (n - 1)) : null;
  }
  return s;
}

const fmtPsi = (v) => `${Number(v).toFixed(1)} PSI`;
const ALERT_RANK = { critical: 4, high: 3, medium: 2, low: 1 };

/**
 * Ranked findings for the FlowSense AI card.
 * @returns {{level:'critical'|'warning'|'info'|'ok', title:string, detail?:string, source:string}[]}
 */
export function buildSensorInsights({
  kind, liveState, lastHeardMs, measuredMs, clockSkew, now, thresholds,
  stats, ai, alerts = [], workOrders = [], sensor = {}, asset = null, registered = true, feedConnected = true,
}) {
  const out = [];
  const push = (level, title, detail, source) => out.push({ level, title, detail, source });

  // 1. Live state
  if (!registered) {
    push('warning', 'Not in the sensor register', 'Readings arrive from this device, but it has no sensor record — register it to link it to an asset and enable alerts.', 'Registry');
  }
  if (liveState === 'no_data') push('warning', 'No reading received yet', 'The node has never reported. Check power, Wi-Fi and the server address on the node.', 'Live feed');
  else if (liveState === 'offline') push('critical', `Offline — no data for ${formatAgo(lastHeardMs, now)?.replace(' ago', '') || 'over 5 min'}`, 'Check power, Wi-Fi coverage and the node display (the serial log shows the HTTP error).', 'Live feed');
  else if (liveState === 'delayed') push('warning', `Delayed — last data ${formatAgo(lastHeardMs, now)}`, `No reading for over ${formatDurationShort(thresholds?.onlineMs ?? 120000)}; the node posts every ${EXPECTED_CADENCE_SEC} s.`, 'Live feed');
  else if (liveState === 'transmitting' || liveState === 'online') push('ok', `${liveState === 'transmitting' ? 'Transmitting' : 'Online'} — last data ${formatAgo(lastHeardMs, now)}`, null, 'Live feed');
  if (!feedConnected) push('info', 'Dashboard live feed disconnected', 'Status is based on the last data received; it resumes updating when the feed reconnects.', 'Live feed');
  if (clockSkew) push('warning', 'Device clock is ahead of server time', 'The node\'s NTP time looks wrong — readings may be stored with future timestamps.', 'Live feed');
  if (measuredMs && lastHeardMs && lastHeardMs - measuredMs > 120000) {
    push('info', 'Latest reading was delivered late', `Measured ${formatAgo(measuredMs, now)} — the node was buffering while offline.`, 'Live feed');
  }

  // 2. Cadence / gaps (needs a few readings)
  if (stats && stats.n >= 5 && stats.medianIntervalSec) {
    if (stats.medianIntervalSec > EXPECTED_CADENCE_SEC * 2.5) {
      push('warning', `Reporting every ~${Math.round(stats.medianIntervalSec)} s`, `Expected every ${EXPECTED_CADENCE_SEC} s — posts are failing or being retried (weak Wi-Fi or server errors).`, 'Cadence');
    }
    if (stats.gaps > 0) {
      push('info', `${stats.gaps} gap${stats.gaps === 1 ? '' : 's'} of ${GAP_SEC} s or more in the last ${stats.n} readings`, `Longest ${formatDurationShort(stats.maxGapSec * 1000)}.`, 'Cadence');
    }
  }

  // 3. Value vs limits
  if (kind === 'pressure' && stats && stats.last !== null && stats.last !== undefined && liveState !== 'no_data') {
    if (stats.last > PSI_HIGH) push('critical', `Pressure ${fmtPsi(stats.last)} — above the ${PSI_HIGH} PSI limit`, stats.above > 1 ? `${stats.above} of the last ${stats.n} readings were above the limit.` : null, 'Limits');
    else if (stats.last < PSI_LOW) push('warning', `Pressure ${fmtPsi(stats.last)} — below the ${PSI_LOW} PSI limit`, stats.below > 1 ? `${stats.below} of the last ${stats.n} readings were below the limit.` : 'Possible leak, closed valve or sensor fault.', 'Limits');
    else if (stats.n >= 5) push('ok', `Pressure within limits (${PSI_LOW}–${PSI_HIGH} PSI)`, `Last ${stats.n} readings: min ${fmtPsi(stats.min)}, avg ${fmtPsi(stats.avg)}, max ${fmtPsi(stats.max)}.`, 'Limits');
  }
  if (kind === 'water_level' && stats && stats.last) {
    if (stats.last === 'HIGH') push('warning', 'Water at the HIGH float', stats.transitions ? `${stats.transitions} level change${stats.transitions === 1 ? '' : 's'} in the last ${stats.n} readings.` : null, 'Limits');
    else if (stats.last === 'EMPTY') push('warning', 'Water below the LOW float (EMPTY)', 'Dry well / tank, or the float switch is stuck or disconnected.', 'Limits');
  }

  // 4. FlowSense AI (flood, health, edge anomaly)
  if (ai?.anomaly) push(ai.anomaly.severity === 'critical' ? 'critical' : 'warning', ai.anomaly.label, ai.anomaly.detail, 'Edge anomaly check');
  if (ai?.flood) {
    const p = Math.round(ai.flood.probability * 100);
    const eta = ai.flood.minutesToOvertop;
    const etaText = eta !== null && eta !== undefined ? (eta <= 0 ? ' — overtopping now' : ` — overtopping in ~${Math.round(eta)} min`) : '';
    if (ai.flood.probability >= 0.65) push('critical', `Flood risk ${p}%${etaText}`, ai.flood.source, 'Flood model');
    else if (ai.flood.probability >= 0.35) push('warning', `Flood risk ${p}%${etaText}`, ai.flood.source, 'Flood model');
    else push('ok', `Flood risk ${p}%`, ai.flood.source, 'Flood model');
  }
  if (ai?.health) {
    const h = ai.health;
    const parts = [h.score !== null ? `health ${Math.round(h.score)}/100` : null, h.status, h.rulDays !== null ? `~${Math.round(h.rulDays)} days remaining life` : null].filter(Boolean).join(' · ');
    const title = `Linked asset ${parts}`;
    push(h.level === 'critical' ? 'critical' : h.level === 'warning' ? 'warning' : 'ok', title, h.level === 'critical' ? 'Plan maintenance or replacement.' : null, 'Asset health model');
  }

  // 5. Alerts / work orders
  const openAlerts = (alerts || []).filter((a) => a && a.status !== 'resolved' && a.status !== 'auto_resolved');
  if (openAlerts.length) {
    const top = [...openAlerts].sort((a, b) => (ALERT_RANK[b.severity] || 0) - (ALERT_RANK[a.severity] || 0))[0];
    const lvl = top.severity === 'critical' || top.severity === 'high' ? 'critical' : 'warning';
    push(lvl, `${openAlerts.length} open alert${openAlerts.length === 1 ? '' : 's'} — ${top.alert_message}`, top.work_order_id ? `Work order ${top.work_order_id} raised.` : 'No work order raised yet.', 'Alerts');
  }
  if ((workOrders || []).length) {
    const wo = workOrders[0];
    push('info', `${workOrders.length} open work order${workOrders.length === 1 ? '' : 's'} on ${asset?.name || 'the linked asset'}`, `${wo.title} (${String(wo.status).replace(/_/g, ' ')}, ${wo.priority})`, 'Work orders');
  }

  // 6. Registry gaps that disable features
  const assetId = sensor?.asset_id || (sensor?.linked_entity_type && !['pipeline', 'none'].includes(sensor.linked_entity_type) ? sensor.linked_entity_id : null);
  if (registered && !assetId) push('info', 'Not linked to an asset', 'Link it to a well, tank or pump to enable the asset health and flood models.', 'Registry');
  const lat = parseFloat(sensor?.latitude);
  const lng = parseFloat(sensor?.longitude);
  if (registered && (!Number.isFinite(lat) || !Number.isFinite(lng))) push('info', 'Not placed on the map', 'Place it so it appears on the network map and in spatial analysis.', 'Registry');
  // model "why not" reasons — skip the bind-to-asset one when "Not linked to an asset" is already listed
  if (ai?.reasons?.length) {
    ai.reasons
      .filter((r) => !(registered && !assetId && /^Bind this sensor/i.test(r)))
      .forEach((r) => push('info', r, null, 'FlowSense AI'));
  }

  return out.sort((a, b) => SEVERITY_RANK[b.level] - SEVERITY_RANK[a.level]);
}

export const INSIGHT_META = Object.freeze({
  critical: { color: 'red', label: 'Action needed' },
  warning: { color: 'orange', label: 'Watch' },
  info: { color: 'blue', label: 'Info' },
  ok: { color: 'green', label: 'Healthy' },
});
